import { useMemo, useState } from 'react';

import { AssetDTO, EntityRefUtil, IssueDTO, IssueSeverity, IssueSource, IssueStatus } from '@gearbelt/global';
import { match } from 'ts-pattern';

import notificationsErrorVector from '@/assets/vectors/notifications-error.svg';
import { ListItem } from '@/awa-pages/[workspaceId]/assets/[id]/issues/list-item';
import IssueDrawer from '@/components/issues/issue-drawer';

export function AWAIssuesList({ issues = [], asset }: { issues: IssueDTO[]; asset: AssetDTO }) {
  const [selectedIssue, setSelectedIssue] = useState<IssueDTO | null>(null);
  const [isIssuesDrawerOpen, setIsIssuesDrawerOpen] = useState(false);

  const groupedIssues = useMemo(() => {
    const groups = new Map<IssueStatus, IssueDTO[]>();

    issues?.forEach((issue: IssueDTO): void => {
      const status: IssueStatus = issue.status as IssueStatus;
      if (!groups.get(status)) {
        groups.set(status, []);
      }
      groups.get(status)!.push(issue);
    });

    return groups;
  }, [issues]);

  const getIssueStatusText = (status: IssueStatus): string => {
    return match(status)
      .with(IssueStatus.Open, () => 'Open')
      .with(IssueStatus.Closed, () => 'Closed')
      .otherwise(() => '');
  };

  /* No issues yet */
  if (issues.length === 0) {
    return (
      <div className="flex h-full w-full flex-col items-center gap-2 px-2 py-25">
        <img src={notificationsErrorVector} alt="No issues yet" className="h-[150px] w-[150px]" />
        <div className="text-default-text text-center text-xl font-medium">No issues yet</div>
        <div className="text-secondary-text text-center text-base font-normal">Tap the green button to get started.</div>
      </div>
    );
  }

  return (
    <>
      <div className="border-grey-2 flex h-full w-full flex-col items-start">
        {Array.from(groupedIssues.entries()).map(([status, issues]) => (
          <div key={status} className="w-full">
            <div className="bg-grey-1 flex h-[28px] w-full flex-row items-center gap-1 p-1">
              <div className="text-default-text text-sm font-medium">{getIssueStatusText(status)}</div>
            </div>
            <div className="flex w-full flex-col items-start bg-white">
              {issues?.map((issue) => (
                <ListItem
                  key={issue.id}
                  issueId={issue.id}
                  issueTitle={issue.title || issue.description || ''}
                  issueFieldReference={issue.fieldReference}
                  issueSeverity={issue.severity as IssueSeverity}
                  issueCreatedBy={EntityRefUtil.createUserFullNameRef(issue.user)}
                  onClick={() => {
                    setSelectedIssue(issue);
                    setIsIssuesDrawerOpen(true);
                  }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      <IssueDrawer
        noTrigger
        asset={asset}
        issueId={selectedIssue?.id}
        source={IssueSource.Manual}
        open={isIssuesDrawerOpen}
        onOpenChange={setIsIssuesDrawerOpen}
        disableOutsideClickDismiss
      />
    </>
  );
}
